import React, { useEffect, useRef, useState } from 'react';
import { map, reverse } from 'lodash';
import { faPaperPlaneTop } from '@fortawesome/pro-light-svg-icons';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spin, Avatar, Button, Input, Modal, message } from 'antd';
import { bool, func, instanceOf } from 'prop-types';

import I18nCustomFormatter from 'Src/common/components/i18nCustomFormatter';
import { getMessages, postMessage } from '../../../actions';
import '../style.scss';

function MessagesModal({ isOpen, closeMessagesModal, connection }) {
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollRef = useRef(null);
  const { mentor, mentee } = connection;
  const primaryData = window.userData.id === mentor?.id ? mentee : mentor;

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollRef.current?.scrollIntoView();
    }, 0);
  };

  useEffect(() => {
    setLoading(true);

    getMessages(connection.id, currentPage)
      .then((res) => {
        setMessages(reverse(res.data.results));
        setHasMore(!!res.data.next);
        setLoading(false);
        scrollToBottom();
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const handleChange = (event) => {
    setNewMessage(event.target.value);
  };

  const handlePostMessage = () => {
    postMessage(connection.id, { message: newMessage })
      .then((res) => {
        const updatedMessages = [...messages];
        updatedMessages.push(res.data);

        setMessages(updatedMessages);
        setNewMessage('');
        scrollToBottom();
      })
      .catch(() => {
        message.error('Failed to send message, something went wrong!');
      });
  };

  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1);
    setLoadingMore(true);

    getMessages(connection.id, currentPage + 1)
      .then((res) => {
        setMessages([...reverse(res.data.results), ...messages]);
        setHasMore(!!res.data.next);
        setLoadingMore(false);
      })
      .catch(() => {
        setLoadingMore(false);
      });
  };

  return (
    <Modal
      width="100%"
      visible={isOpen}
      closable={false}
      onCancel={closeMessagesModal}
      wrapClassName="messages-modal"
      title={
        <div className="arc-d-flex arc-flex-vertical-align">
          <FontAwesomeIcon icon={faArrowLeft} onClick={closeMessagesModal} />
          <div className="ml16 arc-d-flex">
            <Avatar size={21} src={primaryData?.present_picture} />
            <h6 className="arc-H200 ml8">{primaryData?.full_name}</h6>
          </div>
        </div>
      }
      footer={
        <div className="p20 arc-d-flex">
          <Input
            placeholder="Write a response"
            className="message-input"
            value={newMessage}
            onChange={handleChange}
            onPressEnter={handlePostMessage}
          />
          <Button
            disabled={!newMessage}
            shape="circle"
            icon={<FontAwesomeIcon icon={faPaperPlaneTop} className="arc-color-primary" />}
            className="send-message-button ml12"
            onClick={handlePostMessage}
          />
        </div>
      }>
      <Choose>
        <When condition={loading}>
          <Spin />
        </When>
        <Otherwise>
          <div className="arc-d-flex message-modal-content">
            <div className="arc-d-flex messages-wrapper">
              <If condition={hasMore}>
                <Button className="load-more-messages-button" onClick={handleLoadMore} loading={loadingMore}>
                  <I18nCustomFormatter id="load-older-messages" />
                </Button>
              </If>
              {map(messages, (msg) => (
                <div className="message-content arc-d-flex" key={msg.id}>
                  <Avatar size={24} src={msg?.sender?.present_picture} />
                  <div className="ml8">
                    <h6 className="arc-H150 arc-font-weight-bold-imp">{msg.sender.full_name}</h6>
                    <p className="arc-p arc-color-gray10 mt2">{msg.message}</p>
                  </div>
                </div>
              ))}
              <div id="scroll-helper" style={{ height: '1px' }} ref={scrollRef} />
            </div>
          </div>
        </Otherwise>
      </Choose>
    </Modal>
  );
}

MessagesModal.propTypes = {
  isOpen: bool.isRequired,
  closeMessagesModal: func.isRequired,
  connection: instanceOf(Object).isRequired,
};

export default MessagesModal;
